import { Link } from 'react-router'
import { ArrowUpRight, Calendar, MapPin } from 'lucide-react'
import { fileUrl } from '@/lib/pb'
import type { Match, NewsArticle, Player } from '@/lib/types'
import {
  cn,
  formatDateShort,
  formatTime,
  formatWeekday,
  initials,
  POSITION_LABELS,
  plainText,
  readMatch,
  relativeTime,
  stageLabel,
  statusLabel,
  titleCase,
} from '@/lib/utils'

// ---------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------

/**
 * Squad card. The portrait is optional by design — a club rarely has forty
 * photographs on launch day — so the fallback is a full-bleed crest monogram
 * that reads as a deliberate style rather than a broken image.
 */
export function PlayerCard({ player, index = 0 }: { player: Player; index?: number }) {
  const photo = fileUrl(player, player.photo, '800x0')

  return (
    <Link
      to={`/squad/${player.slug}`}
      className="group bg-concrete/10 relative block overflow-hidden focus-visible:outline-offset-4"
      style={{ animationDelay: `${Math.min(index, 12) * 45}ms` }}
    >
      <div className="relative aspect-4/5 overflow-hidden">
        {photo ? (
          <img
            src={photo}
            alt={player.name}
            loading={index < 4 ? 'eager' : 'lazy'}
            decoding="async"
            className="ease-out-expo size-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="from-ink-raised to-ink-sunken flex size-full items-center justify-center bg-gradient-to-b">
            <span className="display text-hairline group-hover:text-blood-deep text-[clamp(3rem,7vw,5rem)] transition-colors duration-500">
              {initials(player.name)}
            </span>
          </div>
        )}

        {/* Legibility scrim: the name sits on top of the photograph. */}
        <div className="from-ink via-ink/25 absolute inset-0 bg-gradient-to-t to-transparent" />

        <div className="absolute top-0 left-0 p-4">
          <div className="display text-blood text-[clamp(1.75rem,3.2vw,2.75rem)] leading-none">
            {String(player.squad_number ?? 0).padStart(2, '0')}
          </div>
          <div className="condensed text-bone/80 mt-1 text-[0.625rem] font-semibold tracking-[0.2em]">
            {POSITION_LABELS[player.position] ?? ''}
          </div>
        </div>

        {player.is_captain ? (
          <span className="condensed border-bone/40 text-bone absolute top-4 right-4 border px-2 py-1 text-[0.625rem] font-bold tracking-[0.16em]">
            Captain
          </span>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 p-4">
          <div className="bg-bone/30 group-hover:bg-blood mb-3 h-px w-full origin-left scale-x-100 transition-colors duration-500" />
          <p className="condensed text-bone text-[0.9375rem] leading-tight font-semibold tracking-[0.06em]">
            {player.name}
          </p>
        </div>
      </div>
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Match
// ---------------------------------------------------------------------------

const RESULT_STYLES = {
  win: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/10',
  draw: 'text-bone-dim border-hairline bg-bone/5',
  loss: 'text-blood-bright border-blood/40 bg-blood/10',
} as const

const RESULT_LABEL = { win: 'W', draw: 'D', loss: 'L' } as const

export function MatchCard({
  match,
  clubName,
  className,
}: {
  match: Match
  clubName: string
  className?: string
}) {
  const view = readMatch(match, clubName)
  const competition = match.expand?.competition
  const homeCrest = fileUrl(match, match.home_crest, '200x200')
  const awayCrest = fileUrl(match, match.away_crest, '200x200')

  return (
    <Link
      to={`/matches/${match.id}`}
      className={cn(
        'group border-hairline bg-ink-raised hover:border-blood/60 relative block border p-5 transition-colors sm:p-6',
        view.isLive && 'border-blood/60',
        className
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {view.isLive ? (
            <span className="flex items-center gap-2">
              <span className="animate-pulse-live bg-blood size-2 rounded-full" aria-hidden />
              <span className="condensed text-blood-bright text-[0.6875rem] font-bold tracking-[0.2em]">
                {match.status === 'halftime' ? 'Half-time' : `Live ${match.live_minute || ''}′`}
              </span>
            </span>
          ) : (
            <span className="condensed text-bone-faint text-[0.6875rem] font-semibold tracking-[0.18em]">
              {competition?.short_name || competition?.name || stageLabel(match.stage) || 'Fixture'}
            </span>
          )}
          {match.group_name ? (
            <span className="condensed border-hairline text-bone-faint border px-2 py-0.5 text-[0.625rem] tracking-[0.14em]">
              {match.group_name}
            </span>
          ) : null}
        </div>

        {view.result ? (
          <span
            className={cn(
              'condensed flex size-6 items-center justify-center border text-[0.6875rem] font-bold',
              RESULT_STYLES[view.result]
            )}
            title={titleCase(view.result)}
          >
            {RESULT_LABEL[view.result]}
          </span>
        ) : null}
      </div>

      <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <TeamSide
          name={match.home_team}
          crest={homeCrest}
          align="right"
          isClub={view.side === 'home'}
        />

        <div className="px-1 text-center">
          {view.hasScore ? (
            <div className="display text-bone text-[clamp(1.75rem,4vw,2.5rem)] leading-none whitespace-nowrap tabular-nums">
              {match.home_score ?? 0}
              <span className="text-bone-faint mx-1.5">–</span>
              {match.away_score ?? 0}
            </div>
          ) : match.status === 'postponed' || match.status === 'cancelled' ? (
            <div className="condensed text-blood-bright text-[0.8125rem] leading-none font-semibold tracking-[0.12em]">
              {statusLabel(match.status)}
            </div>
          ) : (
            <div className="condensed text-bone text-lg leading-none font-semibold">
              {formatTime(match.kickoff)}
            </div>
          )}
        </div>

        <TeamSide
          name={match.away_team}
          crest={awayCrest}
          align="left"
          isClub={view.side === 'away'}
        />
      </div>

      <div className="border-hairline text-bone-faint mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-4 text-[0.8125rem]">
        <span className="flex items-center gap-1.5">
          <Calendar className="size-3.5" aria-hidden />
          {formatWeekday(match.kickoff)} {formatDateShort(match.kickoff)}
        </span>
        {match.venue ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <MapPin className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{match.venue}</span>
          </span>
        ) : null}
        {view.isUpcoming ? (
          <span className="text-blood-bright ml-auto">{relativeTime(match.kickoff)}</span>
        ) : null}
      </div>
    </Link>
  )
}

function TeamSide({
  name,
  crest,
  align,
  isClub,
}: {
  name: string
  crest?: string
  align: 'left' | 'right'
  isClub: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2.5',
        align === 'right' ? 'flex-row-reverse text-right' : 'text-left'
      )}
    >
      {crest ? (
        <img src={crest} alt="" width={32} height={32} className="size-8 shrink-0 object-contain" />
      ) : (
        <span
          className="condensed border-hairline text-bone-faint flex size-8 shrink-0 items-center justify-center border text-[0.625rem] font-bold"
          aria-hidden
        >
          {initials(name)}
        </span>
      )}
      <span
        className={cn(
          'condensed line-clamp-2 min-w-0 text-[0.9375rem] leading-tight font-semibold tracking-[0.04em]',
          isClub ? 'text-bone' : 'text-bone-dim'
        )}
      >
        {name}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// News
// ---------------------------------------------------------------------------

export function NewsCard({
  article,
  featured,
  index = 0,
}: {
  article: NewsArticle
  featured?: boolean
  index?: number
}) {
  const cover = fileUrl(article, article.cover, featured ? '1600x0' : '800x0')
  const summary = article.excerpt || plainText(article.body, featured ? 220 : 130)

  return (
    <Link
      to={`/news/${article.slug}`}
      className={cn(
        'group border-hairline bg-ink-raised hover:border-blood/60 relative flex flex-col overflow-hidden border transition-colors',
        featured && 'md:flex-row'
      )}
    >
      <div
        className={cn(
          'bg-ink-sunken relative overflow-hidden',
          featured ? 'aspect-16/10 md:aspect-auto md:w-1/2' : 'aspect-16/10'
        )}
      >
        {cover ? (
          <img
            src={cover}
            alt=""
            loading={index < 2 ? 'eager' : 'lazy'}
            decoding="async"
            className="ease-out-expo size-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="from-ink-raised to-ink-sunken flex size-full items-center justify-center bg-gradient-to-br">
            <img src="/brand/logo.webp" alt="" className="size-20 opacity-15" />
          </div>
        )}
        {article.category ? (
          <span className="condensed bg-blood text-bone absolute top-3 left-3 px-2.5 py-1 text-[0.625rem] font-bold tracking-[0.16em]">
            {titleCase(article.category)}
          </span>
        ) : null}
      </div>

      <div
        className={cn('flex flex-1 flex-col p-5 sm:p-6', featured && 'md:justify-center md:p-10')}
      >
        <time
          dateTime={article.published_at}
          className="condensed text-bone-faint text-[0.6875rem] tracking-[0.18em]"
        >
          {formatDateShort(article.published_at)} ·{' '}
          {new Date(article.published_at.replace(' ', 'T')).getFullYear()}
        </time>

        <h3
          className={cn(
            'condensed text-balance-safe text-bone group-hover:text-blood-bright mt-3 leading-[1.1] font-bold transition-colors',
            featured ? 'text-[clamp(1.5rem,3vw,2.25rem)]' : 'text-xl'
          )}
        >
          {article.title}
        </h3>

        {summary ? (
          <p className="text-bone-dim mt-3 line-clamp-3 text-[0.9375rem] leading-relaxed">
            {summary}
          </p>
        ) : null}

        <span className="condensed text-blood-bright mt-5 inline-flex items-center gap-1.5 text-[0.75rem] font-semibold tracking-[0.16em]">
          Read
          <ArrowUpRight
            className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            aria-hidden
          />
        </span>
      </div>
    </Link>
  )
}
